<template>
  <div class="row">
    <loading
      :active.sync="isLoading"
      :can-cancel="true"
      :opacity="0.85"
      :is-full-page="true"
    ></loading>
    <div class="col-12">
      <card 
        :title="title" 
        :showFilter="showFilter"
        @doFilter="doFilter"
        :filterInputValue="filterTerm"
      >
        <div slot="raw-content" class="table-responsive">
          <div class="entity-table-container">
            <table class="table table-striped">
              <thead>
                <th v-for="column in columns" :key="column">{{column}}</th>
              </thead>
              <tbody>
                <tr v-for="(item, index) in displayData" :key="index">
                  <td v-html="item.Asset"></td>
                  <td>{{item.Type}}</td>
                  <td>{{item.Area}}</td>
                  <td>{{item.Audited}}</td>
                </tr>
              </tbody>
            </table>
            <div class="row" style="align-items:center">
              <div class="col-md-auto ml-auto">
                <div
                  is="uib-pagination"
                  :boundary-links="true"
                  :total-items="totalItems"
                  @change="changePage"
                  v-model="pagination"
                  class="pagination-sm"
                  previous-text="‹"
                  next-text="›"
                  first-text="«"
                  last-text="»"
                  :max-size="7"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import { db, getCustomer } from "@/db";
import moment from "moment";
import pagination from "vuejs-uib-pagination";
Vue.use(pagination);

import { PaperTable } from "@/components";
import utils from "@/utils";

const tableColumns = ["Asset", "Type", "Area", "Audited"];

export default {
  components: {
    Loading,
    PaperTable
  },
  data() {
    return {
      isLoading: true,
      loading: true,
      title: "Asset Audit Progress",
      columns: [...tableColumns],
      allData: [],
      displayData: [],
      filteredData: [],
      currentPage: 1,
      pagination: {
        currentPage: 1
      },
      totalItems: 1,
      startOfMonth: 0,
      siteId: '',
      showFilter: true,
      filterTerm: '',
      siteName: ''
    };
  },
  async created() {
    this.siteId = this.$route.params.id;
    this.startOfMonth = moment.utc().startOf("month").unix();
    await this.loadSite();
    await this.loadAssets();
  },
  methods: {
    async loadSite() {
      try {
        const siteDoc = await getCustomer()
          .collection("sites")
          .doc(this.siteId)
          .get();
        
        if (siteDoc.exists) {
          const siteData = siteDoc.data();
          this.siteName = siteData.SiteName;
          this.title = `Asset Audit Progress - ${this.siteName}`;
        } else {
          console.error("Site document not found");
        }
      } catch (error) {
        console.error("Error loading site:", error);
      }
    },
    itemValue(item, column) {
      const mapping = {
        'Asset': 'AssetID',
        'Area': 'AreaName',
        'Type': 'AssetTypeName',
        'Audited': 'Audited'
      };
      return item[mapping[column]];
    },
    async loadAssets() {
      try {
        // Get all assets for the site
        const assetsSnapshot = await getCustomer()
          .collection("assets")
          .where("SiteId", "==", this.siteId)
          .get();

        // Get all audits for the current month
        const auditsSnapshot = await getCustomer()
          .collection("audits")
          .where("SiteId", "==", this.siteId)
          .where("DateCompletedUnix", ">=", this.startOfMonth)
          .get();

        // Create a set of audited asset IDs
        const auditedAssetIds = new Set();
        auditsSnapshot.forEach(doc => {
          auditedAssetIds.add(doc.data().AssetId);
        });

        // Map assets with their audit status
        this.allData = [];
        assetsSnapshot.forEach(doc => {
          const assetData = doc.data();
          const assetType = assetData.AssetTypeName || 'Unknown Type';
          this.allData.push({
            Asset: utils.getTypeHtml(assetType, assetData.TagNo),
            Area: assetData.AreaName || 'Unknown Area',
            Type: assetType,
            Audited: auditedAssetIds.has(assetData.Id) ? 'Yes' : 'No'
          });
        });

        this.filterData();
        this.loading = false;
        this.isLoading = false;
      } catch (error) {
        console.error("Error loading assets:", error);
        this.loading = false;
        this.isLoading = false;
      }
    },
    loadPage(num) {
      this.isLoading = true;
      if (!num) num = this.currentPage;
      let take = 10;
      this.displayData = utils.paginate(this.filteredData, take, num);
      this.isLoading = false;
    },
    changePage() {
      const pageNum = this.pagination.currentPage;
      this.currentPage = pageNum;
      this.loadPage(pageNum);
    },
    doFilter(filter) {
      this.filterTerm = filter;
      this.filterData();
    },
    filterData() {
      var search = this.filterTerm.toUpperCase();
      var result = this.allData.filter(f => {
        return (
          //f.Asset.toString().toUpperCase().includes(search) ||
          f.Area.toUpperCase().includes(search) ||
          f.Type.toUpperCase().includes(search) ||
          f.Audited.toUpperCase().includes(search)
        );
      });

      this.filteredData = result.sort((a, b) => 
        a.Area.toUpperCase() > b.Area.toUpperCase() ? 1 : -1
      );

      this.totalItems = this.filteredData.length;
      this.loadPage(1);
    },
    
  }
};
</script>

<style>
.table-striped > thead > tr > th,
.table-striped > tbody > tr > th,
.table-striped > tfoot > tr > th,
.table-striped > thead > tr > td,
.table-striped > tbody > tr > td,
.table-striped > tfoot > tr > td {
  padding: 12px !important;
}

.type-icon {
  font-size: 25px;
  color: #444444;
  width: 30px;
}

.tagnumber {
  margin-left: 10px;
}

</style> 

<style lang="scss">
$table-line-color: #ccc5b9 !default;
$muted-color: #a49e93 !default;
$medium-pale-bg: #f1eae0 !default;
$dark-gray: #9a9a9a !default;

.pagination > li > a:focus,
.pagination > li > a:hover,
.pagination > li > span:focus,
.pagination > li > span:hover {
  z-index: 3;
  color: $medium-pale-bg;
  background-color: $table-line-color;
  border-color: $muted-color;
}

.pagination > .active > a,
.pagination > .active > a:focus,
.pagination > .active > a:hover,
.pagination > .active > span,
.pagination > .active > span:focus,
.pagination > .active > span:hover {
  z-index: 3;
  color: #fff;
  cursor: default;
  background-color: $dark-gray;
  border-color: $muted-color;
}
</style>